/**
 * Giphy Tool for Editor.js
 * Allows embedding GIFs from Giphy URLs
 */
export default class GiphyTool {
  static get toolbox() {
    return {
      title: 'Giphy',
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 4h16v16H4V4zm2 2v12h12V6H6zm2 2h8v8H8V8zm2 2v4h4v-4h-4z" fill="currentColor"/></svg>'
    }
  }

  static get isReadOnlySupported() {
    return true
  }

  constructor({ data, api, readOnly }) {
    this.api = api
    this.readOnly = readOnly
    this.data = {
      url: data.url || '',
      caption: data.caption || '',
      gifUrl: data.gifUrl || '',
      title: data.title || ''
    }
    
    this.wrapper = null
    this.urlInput = null
  }

  render() {
    this.wrapper = document.createElement('div')
    this.wrapper.classList.add('giphy-tool')

    if (this.data.gifUrl) {
      this._renderGif()
    } else {
      this._renderUrlInput()
    }

    return this.wrapper
  }

  _renderUrlInput() {
    const container = document.createElement('div')
    container.classList.add('giphy-tool__input-container')

    const input = document.createElement('input')
    input.classList.add('giphy-tool__url-input')
    input.placeholder = 'Paste Giphy URL (e.g., https://giphy.com/gifs/...)'
    input.value = this.data.url
    input.addEventListener('paste', this._handlePaste.bind(this))
    input.addEventListener('keydown', this._handleKeydown.bind(this))

    const button = document.createElement('button')
    button.type = 'button'
    button.classList.add('giphy-tool__embed-button')
    button.textContent = 'Embed GIF'
    button.addEventListener('click', this._embedGif.bind(this))

    container.appendChild(input)
    container.appendChild(button)
    this.wrapper.appendChild(container)

    this.urlInput = input
    
    // Focus the input
    setTimeout(() => input.focus(), 100)
  }

  _renderGif() {
    const container = document.createElement('div')
    container.classList.add('giphy-tool__gif-container')

    const img = document.createElement('img')
    img.src = this.data.gifUrl
    img.alt = this.data.title || 'Giphy GIF'
    img.classList.add('giphy-tool__gif')

    const caption = document.createElement('div')
    caption.classList.add('giphy-tool__caption')
    caption.contentEditable = !this.readOnly
    caption.innerHTML = this.data.caption || ''
    caption.setAttribute('data-placeholder', 'Add a caption (optional)')
    
    if (!this.readOnly) {
      caption.addEventListener('blur', () => {
        this.data.caption = caption.innerHTML
      })
    }

    container.appendChild(img)
    container.appendChild(caption)

    if (!this.readOnly) {
      const controls = document.createElement('div')
      controls.classList.add('giphy-tool__controls')
      
      const changeButton = document.createElement('button')
      changeButton.type = 'button'
      changeButton.textContent = 'Change GIF'
      changeButton.classList.add('giphy-tool__change-button')
      changeButton.addEventListener('click', () => {
        this.data.url = ''
        this.data.gifUrl = ''
        this.data.title = ''
        this.wrapper.innerHTML = ''
        this._renderUrlInput()
      })
      
      controls.appendChild(changeButton)
      container.appendChild(controls)
    }

    this.wrapper.appendChild(container)
  }

  _handlePaste(event) {
    const pastedText = (event.clipboardData || window.clipboardData).getData('text')
    if (this._isGiphyUrl(pastedText)) {
      event.preventDefault()
      this.urlInput.value = pastedText
      this.data.url = pastedText
      setTimeout(() => this._embedGif(), 100)
    }
  }

  _handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this._embedGif()
    }
  }

  async _embedGif() {
    const url = this.urlInput ? this.urlInput.value : this.data.url
    
    if (!this._isGiphyUrl(url)) {
      this._showError('Please enter a valid Giphy URL. Examples:\n• https://giphy.com/gifs/title-abc123\n• https://i.giphy.com/abc123.gif')
      return
    }

    try {
      const gifData = await this._extractGifData(url)
      if (gifData) {
        this.data.url = url
        this.data.gifUrl = gifData.gifUrl
        this.data.title = gifData.title
        
        this.wrapper.innerHTML = ''
        this._renderGif()
        
        // Trigger change event
        this.api.blocks.getBlockByIndex(this.api.blocks.getCurrentBlockIndex()).dispatchChange()
      } else {
        this._showError('Could not load GIF from this URL')
      }
    } catch (error) {
      console.error('Error embedding Giphy:', error)
      this._showError('Error loading GIF. Please try again.')
    }
  }

  _isGiphyUrl(url) {
    if (!url || typeof url !== 'string') return false
    
    const giphyPatterns = [
      /^https?:\/\/giphy\.com\/gifs\//,
      /^https?:\/\/media\.giphy\.com\/media\//,
      /^https?:\/\/i\.giphy\.com\//,
      /^https?:\/\/gph\.is\//
    ]
    
    return giphyPatterns.some(pattern => pattern.test(url))
  }

  async _extractGifData(url) {
    try {
      // Try to extract GIF ID from various Giphy URL formats
      let gifId = null
      
      // Handle giphy.com/gifs/name-id format
      // Extract the ID from the end of the path
      const giphyMatch = url.match(/giphy\.com\/gifs\/.*?([a-zA-Z0-9]{8,})(?:\?|#|$)/)
      if (giphyMatch) {
        // Take the last part if there are multiple matches (handles complex URLs)
        const pathParts = url.split('/gifs/')[1].split(/[\?#]/)[0].split('-')
        gifId = pathParts[pathParts.length - 1]
      }
      
      // Handle media.giphy.com format
      const mediaMatch = url.match(/media\.giphy\.com\/media\/([a-zA-Z0-9]+)/)
      if (mediaMatch) {
        gifId = mediaMatch[1]
      }
      
      // Handle i.giphy.com format
      const iGiphyMatch = url.match(/i\.giphy\.com\/([a-zA-Z0-9]+)\.gif/)
      if (iGiphyMatch) {
        gifId = iGiphyMatch[1]
      }
      
      // Handle gph.is short URLs - these would need to be resolved
      if (url.includes('gph.is')) {
        // For short URLs, we'll try to use the URL directly as a fallback
        return {
          gifUrl: url,
          title: 'Giphy GIF'
        }
      }
      
      if (gifId) {
        // Construct the direct GIF URL
        const gifUrl = `https://i.giphy.com/${gifId}.gif`
        
        return {
          gifUrl: gifUrl,
          title: 'Giphy GIF'
        }
      }
      
      // Fallback: try to use the URL directly if it ends with .gif
      if (url.endsWith('.gif')) {
        return {
          gifUrl: url,
          title: 'Giphy GIF'
        }
      }
      
      return null
    } catch (error) {
      console.error('Error extracting GIF data:', error)
      return null
    }
  }

  _showError(message) {
    // Create a temporary error message
    const error = document.createElement('div')
    error.classList.add('giphy-tool__error')
    
    // Handle multiline messages
    if (message.includes('\n')) {
      const lines = message.split('\n')
      lines.forEach((line, index) => {
        if (index > 0) error.appendChild(document.createElement('br'))
        error.appendChild(document.createTextNode(line))
      })
    } else {
      error.textContent = message
    }
    
    if (this.urlInput) {
      // Remove any existing error messages first
      const existingErrors = this.urlInput.parentNode.querySelectorAll('.giphy-tool__error')
      existingErrors.forEach(err => err.remove())
      
      this.urlInput.parentNode.appendChild(error)
      setTimeout(() => {
        if (error.parentNode) {
          error.parentNode.removeChild(error)
        }
      }, 5000) // Longer timeout for more detailed message
    }
  }

  save() {
    return {
      url: this.data.url,
      gifUrl: this.data.gifUrl,
      caption: this.data.caption,
      title: this.data.title
    }
  }

  static get sanitize() {
    return {
      url: {},
      gifUrl: {},
      caption: {
        b: {},
        i: {},
        strong: {},
        em: {},
        a: {
          href: true,
          target: '_blank'
        }
      },
      title: {}
    }
  }
} 